// ModalContext.js
import React, { createContext, useContext, useState } from 'react';
import { ThemeProvider } from '@emotion/react';

// Ant Design theme variables
const antDesignTheme = {
  colors: {
    primary: '#1890ff',
    success: '#52c41a',
    warning: '#faad14',
    error: '#f5222d',
    text: 'rgba(0, 0, 0, 0.85)',
    textSecondary: 'rgba(0, 0, 0, 0.45)',
    bgContainer: '#ffffff',
    bgLayout: '#f0f2f5',
    border: '#d9d9d9',
    split: 'rgba(0, 0, 0, 0.06)',
    fillQuaternary: 'rgba(0, 0, 0, 0.02)',
  },
  spacing: {
    marginXs: '8px',
    marginSm: '12px',
    marginMd: '16px',
    marginLg: '24px',
    paddingXs: '8px',
    paddingSm: '12px',
    paddingMd: '16px',
    paddingLg: '24px',
  },
  typography: {
    fontSizeSm: '12px',
    fontSizeBase: '14px',
    fontSizeLg: '16px',
  },
  borderRadius: {
    sm: '2px',
    base: '4px',
    lg: '8px',
  },
  boxShadow:
    '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
  // CSS variables for direct use
  cssVariables: {
    '--ant-color-primary': '#1890ff',
    '--ant-color-success': '#52c41a',
    '--ant-color-warning': '#faad14',
    '--ant-color-error': '#f5222d',
    '--ant-color-text': 'rgba(0, 0, 0, 0.85)',
    '--ant-color-text-secondary': 'rgba(0, 0, 0, 0.45)',
    '--ant-color-border': '#d9d9d9',
    '--ant-color-split': 'rgba(0, 0, 0, 0.06)',
    '--ant-color-bg-container': '#ffffff',
    '--ant-color-bg-layout': '#f0f2f5',
    '--ant-color-fill-quaternary': 'rgba(0, 0, 0, 0.02)',
    '--ant-margin-xs': '8px',
    '--ant-margin-sm': '12px',
    '--ant-margin-md': '16px',
    '--ant-margin-lg': '24px',
    '--ant-padding-xs': '8px',
    '--ant-padding-sm': '12px',
    '--ant-padding-md': '16px',
    '--ant-padding-lg': '24px',
    '--ant-font-size-sm': '12px',
    '--ant-font-size-base': '14px',
    '--ant-font-size-lg': '16px',
    '--ant-border-radius-sm': '2px',
    '--ant-border-radius-base': '4px',
    '--ant-border-radius-lg': '8px',
    '--ant-box-shadow':
      '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
  },
};

const ModalContext = createContext();

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    // Instead of throwing, return a default context
    return { isOpen: false, toggle: () => {} };
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [placeholder, setPlaceholder] = useState('Your message');

  const toggle = (newPlaceholder = 'Your message') => {
    setIsOpen(!isOpen);
    setPlaceholder(newPlaceholder);
  };

  return (
    <ModalContext.Provider value={{ isOpen, toggle, placeholder, setIsOpen }}>
      <ThemeProvider theme={antDesignTheme}>{children}</ThemeProvider>
    </ModalContext.Provider>
  );
};
